import { useEffect, useState, type FormEvent } from "react";
import { notificationsApi } from "../api.js";
import { useAuth } from "../auth/AuthContext.js";
import { Button, Card, EmptyState, ErrorBanner, Field, PageHeader, Select, Spinner, TextInput } from "../components/ui.js";

interface PreferenceDto { notificationType: string; preferredChannel: string; emailEnabled: boolean; smsEnabled: boolean; pushEnabled: boolean; inAppEnabled: boolean; }

export function formatNotificationType(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function SettingsPage() {
  const [prefs, setPrefs] = useState<PreferenceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const load = async () => {
    const res = await notificationsApi.preferences();
    if (!res.ok) setError(res.error?.errorCode ?? "Failed to load preferences");
    else setPrefs(res.data as PreferenceDto[] ?? []);
  };
  useEffect(() => { void load(); }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!user) return <Spinner />;

  const updatePref = async (p: PreferenceDto) => {
    const res = await notificationsApi.updatePreference(p);
    if (!res.ok) setError(res.error?.errorCode ?? "Update failed");
    else void load();
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Your account"
        title="Settings"
        description="Review account details and choose how Nettstay should contact you."
      />
      <Card title="Account details" description="The identity currently signed in to this workspace.">
        <dl className="summary-grid">
          <dt>Email</dt><dd>{user.email}</dd>
          <dt>Access</dt><dd>{user.role}</dd>
        </dl>
      </Card>
      <Card title="Notification preferences" description="Control the preferred channel for supported account notifications.">
        {!prefs ? <Spinner /> : prefs.length === 0 ? <EmptyState title="No preferences configured" message="Add a supported notification type below to choose how it reaches you." /> : (
          <div className="pref-list">
            {prefs.map((p) => <PreferenceRow key={p.notificationType} pref={p} onChange={updatePref} />)}
          </div>
        )}
        <NewPreferenceForm onSaved={load} />
      </Card>
    </div>
  );
}

function PreferenceRow({ pref, onChange }: { pref: PreferenceDto; onChange: (p: PreferenceDto) => void }) {
  const [local, setLocal] = useState(pref);
  return (
    <div className="pref-row">
      <strong>{formatNotificationType(pref.notificationType)}</strong>
      <Select aria-label={`${formatNotificationType(pref.notificationType)} channel`} value={local.preferredChannel} onChange={(e) => setLocal({ ...local, preferredChannel: e.target.value })}>
        <option value="email">Email</option><option value="sms">SMS</option><option value="push">Push</option><option value="in-app">In-app</option>
      </Select>
      <Button onClick={() => onChange(local)}>Save</Button>
    </div>
  );
}

function NewPreferenceForm({ onSaved }: { onSaved: () => void }) {
  const [form, setForm] = useState<PreferenceDto>({ notificationType: "", preferredChannel: "email", emailEnabled: true, smsEnabled: false, pushEnabled: false, inAppEnabled: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setBusy(true);
    const res = await notificationsApi.updatePreference(form);
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Failed"); return; }
    setForm({ ...form, notificationType: "" }); onSaved();
  };

  return (
    <form onSubmit={onSubmit} className="form-inline preference-form">
      {error && <ErrorBanner message={error} />}
      <Field label="Notification type"><TextInput required value={form.notificationType} onChange={(e) => setForm({ ...form, notificationType: e.target.value })} placeholder="weekly_summary" /></Field>
      <Field label="Channel"><Select value={form.preferredChannel} onChange={(e) => setForm({ ...form, preferredChannel: e.target.value })}>
        <option value="email">Email</option><option value="sms">SMS</option><option value="push">Push</option><option value="in-app">In-app</option>
      </Select></Field>
      <Button type="submit" disabled={busy}>{busy ? "Adding…" : "Add preference"}</Button>
    </form>
  );
}
